import React, { memo, useMemo, useState } from "react";
import _ from "lodash";

import { ChevronLeftOutlined } from "@toddle-edu/ds-icons";
import {
  Button,
  DropdownMenu,
  EmptyState,
  IconButton,
  Modal,
  SplitButtonDropdown,
} from "@toddle-edu/ds-web";
import { NoDataIllustration } from "@toddle-edu/ds-theme";

import ACLStore from "lib/aclStore";
import { useI18n } from "Hooks";
import DatasetTaggingBody from "AppComponents/DatasetTaggingBody";
import { ENABLE_NEW_TAGGING_MODAL } from "Constants/permissionConstants";

import StandardsDataGrid from "./components/StandardsDataGrid";
import BreadCrumbs from "./components/BreadCrumbs";
import HeaderDropdown from "../HeaderDropdown";
import classes from "./SelectStandardsModal.scss";
import useFilterQueries from "../../hooks/useFilterQueries";
import {
  NODE_SOURCES,
  PRS_QUICK_COPY_APPLY_SCOPES,
} from "../../modules/stringConstants";
import { getApplyScopeMap, getApplyOptions } from "../../modules/utils";

const styles = {
  modalHeaderStyle: { marginBottom: 0 },
  modalBodyStyle: {
    minHeight: 0,
    height: "100%",
    padding: "0px",
  },
};

const TEST_ID_PREFIX = "planning-selectStandardsModal";

const SelectStandardsModal = props => {
  const {
    onClose,
    setStandardSetId,
    standardSetId,
    label,
    standardEdges,
    idsToPropertiesMap,
    selectedNodesSource,
    setSelectedNodesSource,
    learningCourseId,
    hideBackAndBreadcrumbs = false,
    columns,
    selectedStandards,
    setSelectedStandards,
    onSave,
    updateTree,
    plannerElementType,
    initialFilters,
    treeMapRef,
    resolvedMinimalTree,
    selectedApplyScope,
  } = props;

  const { t } = useI18n(["common", "scopeAndSequence", "course"]);

  const [applyScope, setApplyScope] = useState(
    selectedApplyScope || PRS_QUICK_COPY_APPLY_SCOPES.SINGLE_STUDENT
  );

  const showNewCommonComponent = ACLStore.can(ENABLE_NEW_TAGGING_MODAL);

  const { subjects, grades, tags, isData, isLoading } = useFilterQueries({
    skip: showNewCommonComponent,
  });

  const currentStandardSet = useMemo(
    () => idsToPropertiesMap?.[standardSetId] || {},
    [idsToPropertiesMap, standardSetId]
  );
  const { title, totalCount } = currentStandardSet;

  const applyScopeMap = getApplyScopeMap({ t });
  const applyOptions = getApplyOptions({ t });
  const selectedStandardsCount = selectedStandards.size;
  const isQuickCopyUpdateEnabled = Boolean(selectedApplyScope);

  const onBackClick = () => {
    setApplyScope(
      selectedApplyScope || PRS_QUICK_COPY_APPLY_SCOPES.SINGLE_STUDENT
    );
    setStandardSetId(null);
  };

  const handleSave = async ({ applyScopeOverride } = {}) => {
    const effectiveScope = applyScopeOverride ?? "";
    const ancestorTree = treeMapRef.current.getAncestorTree?.();

    if (_.isFunction(onSave)) {
      await onSave({
        selectedRows: Array.from(selectedStandards),
        nodes: ancestorTree,
        applyScope: effectiveScope,
      });
    }
    if (effectiveScope === PRS_QUICK_COPY_APPLY_SCOPES.SINGLE_STUDENT) {
      onClose();
    }
  };

  const handleApplyScopeClick = async ({ key }) => {
    setApplyScope(key);
    await handleSave({ applyScopeOverride: key });
  };

  const showLoaderOverHeader = !isData && isLoading;

  const showCourseStandards =
    selectedNodesSource?.value === NODE_SOURCES.COURSE;

  const dynamicFilters = useMemo(
    () =>
      showCourseStandards ? { learningCourseIds: [learningCourseId] } : {},
    [showCourseStandards, learningCourseId]
  );

  const renderModalBody = () => {
    if (totalCount === 0) {
      return (
        <EmptyState
          illustration={NoDataIllustration}
          title={t("common:no_label_has_been_added_yet", {
            label: t("common:standard_other"),
          })}
        />
      );
    }
    if (showNewCommonComponent) {
      return (
        <DatasetTaggingBody
          plannerElementNodeSetId={standardSetId}
          initialFilters={initialFilters}
          dynamicFilters={dynamicFilters}
          selectedRows={selectedStandards}
          updateSelectedRows={setSelectedStandards}
          plannerElementType={plannerElementType}
          config={{ moduleType: "UnitPlanV2" }}
          label={label}
          treeMapRef={treeMapRef}
          resolvedMinimalTree={resolvedMinimalTree}
        />
      );
    }
    return (
      <StandardsDataGrid
        id={standardSetId}
        selectedStandards={selectedStandards}
        setSelectedStandards={setSelectedStandards}
        subjects={subjects}
        grades={grades}
        tags={tags}
        showLoaderOverHeader={showLoaderOverHeader}
        showCourseStandards={showCourseStandards}
        learningCourseId={learningCourseId}
        columns={columns}
        updateTree={updateTree}
      />
    );
  };
  return (
    <Modal isOpen onClose={onClose}>
      <Modal.Header
        testId={TEST_ID_PREFIX}
        hasSeparator
        style={styles.modalHeaderStyle}
        actionElement={
          <HeaderDropdown
            selectedNodesSource={selectedNodesSource}
            onChange={setSelectedNodesSource}
          />
        }
      >
        <div className={classes.header}>
          {hideBackAndBreadcrumbs ? null : (
            <IconButton
              icon={<ChevronLeftOutlined />}
              type="plain"
              variant="neutral"
              onClick={onBackClick}
              testId={`${TEST_ID_PREFIX}-back`}
            />
          )}
          <div>
            <h6>{title}</h6>
            {hideBackAndBreadcrumbs ? null : (
              <BreadCrumbs
                standardEdges={standardEdges}
                setStandardSetId={setStandardSetId}
                standardSetId={standardSetId}
                title={title}
                label={label}
              />
            )}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body style={styles.modalBodyStyle}>{renderModalBody()}</Modal.Body>
      <Modal.Footer hasSeparator>
        <div className={classes.footer}>
          <div className="text-body">
            {t("common:count_label_selected", {
              count: selectedStandardsCount,
              label: t("common:standard", {
                count: selectedStandardsCount,
              }),
            })}
          </div>

          <div className={classes.btnContainer}>
            {isQuickCopyUpdateEnabled ? (
              <SplitButtonDropdown
                disabled={selectedStandardsCount === 0}
                overlay={
                  <DropdownMenu
                    options={applyOptions}
                    onClick={handleApplyScopeClick}
                    selectionType="tick"
                    showSelection
                    value={applyScope}
                  />
                }
              >
                <Button
                  variant="progressive"
                  disabled={selectedStandardsCount === 0}
                  testId={`${TEST_ID_PREFIX}-save`}
                  onClick={() => handleApplyScopeClick({ key: applyScope })}
                >
                  {applyScopeMap[applyScope]}
                </Button>
              </SplitButtonDropdown>
            ) : (
              <>
                <Button
                  type="outlined"
                  variant="neutral"
                  onClick={onClose}
                  testId={`${TEST_ID_PREFIX}-cancel`}
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  variant="progressive"
                  testId={`${TEST_ID_PREFIX}-save`}
                  onClick={handleSave}
                >
                  {t("common:save")}
                </Button>
              </>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default memo(SelectStandardsModal);
